import { useState, useEffect, useCallback } from 'react';

export interface CMSContent {
  content: string;
  language: string;
}

interface UseCMSContentOptions {
  /**
   * When true, fetches content immediately. Typically tied to modal visibility.
   */
  enabled?: boolean;
}

interface UseCMSContentResult {
  content: CMSContent | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching CMS page content.
 * 
 * @param view - The CMS page view name (e.g., 'terms-conditions', 'privacy-policy')
 * @param options - Configuration options
 * @returns Content, loading state, error, and refetch function
 * 
 * @example
 * ```tsx
 * const { content, loading, error } = useCMSContent('terms-conditions', { enabled: show });
 * ```
 */
export function useCMSContent(
  view: string,
  options: UseCMSContentOptions = {}
): UseCMSContentResult {
  const { enabled = true } = options;
  
  const [content, setContent] = useState<CMSContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/site/page?view=${encodeURIComponent(view)}&json=1`);
      if (!response.ok) {
        throw new Error(`Failed to fetch CMS content: ${response.status}`);
      }
      const data = await response.json();
      setContent(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error fetching CMS content');
      setError(error);
      console.error(`Error fetching CMS content for view "${view}":`, error);
    } finally {
      setLoading(false);
    }
  }, [view]);

  useEffect(() => {
    // Only fetch if enabled and we don't have content yet
    if (enabled && !content && !loading) {
      fetchContent();
    }
  }, [enabled, content, loading, fetchContent]);

  return { content, loading, error, refetch: fetchContent };
}

export default useCMSContent;
